import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { PinIcon, PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import { useInventoryColumnStore } from '@/stores';
import { L2CP_GROUP_ID, L2CP_COLUMN_IDS } from '@/stores/inventory/inventory-column-store';
import { ContextMenu, useContextMenu } from '@/components/ui/ContextMenu';
import { addSidebarPrefix, useCrossDnd } from './CrossDndContext';
import { useUIStore } from '@/stores/ui/ui-store';
import { clsx } from 'clsx';

// No-op strategy: items do not displace during drag. Visual feedback is
// provided by the DragOverlay ghost + the blue insertion indicator (indicatorTop).
const noDisplacementStrategy: SortingStrategy = () => null;

type DisplayColumn =
  | { type: 'column'; id: string; label: string; visible: boolean; groupId?: string }
  | { type: 'group'; id: string; label: string; visible: boolean };

const buildDisplayColumns = (
  columns: { id: string; label: string; visible: boolean; groupId?: string }[]
): DisplayColumn[] => {
  const result: DisplayColumn[] = [];
  const seenIds = new Set<string>();
  let l2cpEmitted = false;
  for (const col of columns) {
    if (col.groupId === L2CP_GROUP_ID) {
      if (!l2cpEmitted) {
        l2cpEmitted = true;
        if (seenIds.has('lentToCounterparties')) continue;
        seenIds.add('lentToCounterparties');
        const mainCol = columns.find((c) => c.id === 'lentToCounterparties');
        result.push({
          type: 'group',
          id: 'lentToCounterparties',
          label: 'Lent to Counterparties',
          visible: mainCol?.visible ?? true,
        });
      }
      continue;
    }
    if (seenIds.has(col.id)) continue;
    seenIds.add(col.id);
    result.push({ type: 'column', ...col });
  }
  return result;
};

const L2CP_NESTED_IDS = L2CP_COLUMN_IDS.filter((id) => id !== 'lentToCounterparties');

type PinButtonProps = {
  columnId: string;
};

const PinButton: React.FC<PinButtonProps> = ({ columnId }) => {
  const pinColumn = useInventoryColumnStore((s) => s.pinColumn);
  const getPinState = useInventoryColumnStore((s) => s.getPinState);
  const currentPin = getPinState(columnId);
  const { open, position, handleContextMenu, close } = useContextMenu();
  return (
    <>
      <button
        type="button"
        onClick={handleContextMenu}
        className={clsx(
          'flex-shrink-0 rounded p-0.5 transition-colors',
          currentPin ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 hover:text-gray-600'
        )}
        title="Pin column"
        aria-label="Pin column"
      >
        {currentPin === 'left' ? <PinLeftIcon className="h-3.5 w-3.5" /> : currentPin === 'right' ? <PinRightIcon className="h-3.5 w-3.5" /> : <PinIcon className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <ContextMenu
          position={position}
          onClose={close}
          items={[
            {
              label: 'Pin left',
              icon: <PinLeftIcon className="h-3.5 w-3.5" />,
              checked: currentPin === 'left',
              onClick: () => pinColumn(columnId, currentPin === 'left' ? null : 'left'),
            },
            {
              label: 'Pin right',
              icon: <PinRightIcon className="h-3.5 w-3.5" />,
              checked: currentPin === 'right',
              onClick: () => pinColumn(columnId, currentPin === 'right' ? null : 'right'),
            },
            {
              label: 'Unpin',
              dividerBefore: true,
              checked: currentPin === null,
              onClick: () => pinColumn(columnId, null),
            },
          ]}
        />
      )}
    </>
  );
};

type SortableColumnRowProps = {
  id: string;
  label: string;
  visible: boolean;
  onToggleVisibility: (id: string) => void;
  isHidden: boolean;
  showPin?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
};

const SortableColumnRow: React.FC<SortableColumnRowProps> = ({
  id,
  label,
  visible,
  onToggleVisibility,
  isHidden,
  showPin = true,
  scrollContainerRef,
}) => {
  const sortableId = addSidebarPrefix(id);
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id: sortableId });
  const { sidebarOverColumnId, activeDragId, activeDragSource } = useCrossDnd();
  const sidebarHighlightColumnId = useUIStore((s) => s.sidebarHighlightColumnId);

  // Highlight when a table column is being dragged and hovering over this sidebar row
  const isTableDragHighlight = sidebarOverColumnId === sortableId && activeDragSource === 'table';

  // Blueish background for the sidebar item that corresponds to the actively-dragged table column
  const isActiveTableDragItem = activeDragSource === 'table' && activeDragId === id;

  // Brief highlight when pin/hide is triggered from header context menu
  const isContextMenuHighlight = sidebarHighlightColumnId === id;

  // Scroll into view when highlighted from context menu
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isContextMenuHighlight && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isContextMenuHighlight]);

  // Auto-scroll: bring this row into view when it becomes the hovered target during a table drag or sidebar-internal drag
  useEffect(() => {
    if (sidebarOverColumnId === sortableId && activeDragSource != null && scrollContainerRef?.current && scrollRef.current) {
      const container = scrollContainerRef.current;
      const el = scrollRef.current;
      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const containerTop = container.scrollTop;
      const containerBottom = containerTop + container.clientHeight;
      if (elTop < containerTop) {
        container.scrollTop = elTop;
      } else if (elBottom > containerBottom) {
        container.scrollTop = elBottom - container.clientHeight;
      }
    }
  }, [sidebarOverColumnId, sortableId, activeDragSource, scrollContainerRef]);

  // Merge useSortable's setNodeRef with the scrollRef (callback ref pattern)
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      scrollRef.current = node;
    },
    [setNodeRef]
  );

  // No transform needed — no-op strategy produces no displacement.
  // DragOverlay handles the visual ghost.
  return (
    <div
      ref={mergedRef}
      data-sortable-id={sortableId}
      className={clsx(
        'relative flex items-center gap-1.5 px-2 py-2 rounded text-sm transition-colors duration-150',
        isContextMenuHighlight ? 'animate-column-highlight-out bg-blue-100' : isActiveTableDragItem ? 'bg-blue-50' : 'bg-white',
        isDragging && 'opacity-0 pointer-events-none',
        isHidden && 'hidden'
      )}
    >
      {isTableDragHighlight && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10" />
      )}
      <button
        type="button"
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        tabIndex={0}
      >
        <Bars3Icon className="h-4 w-4" />
      </button>
      <span
        className={clsx(
          'flex-1 min-w-0 truncate select-none',
          visible ? 'text-gray-700' : 'text-gray-400'
        )}
        title={label}
      >
        {label}
      </span>
      {showPin && <PinButton columnId={id} />}
      <button
        type="button"
        onClick={() => onToggleVisibility(id)}
        className={clsx(
          'flex-shrink-0 rounded p-0.5 transition-colors',
          visible ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 hover:text-gray-500'
        )}
        title={visible ? 'Hide column' : 'Show column'}
        aria-label={visible ? 'Hide column' : 'Show column'}
      >
        {visible ? (
          <EyeIcon className="h-3.5 w-3.5" />
        ) : (
          <EyeSlashIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
};

const SIDEBAR_L2CP_DND_PREFIX = 'sidebar:l2cp:';

type NestedColumnRowProps = {
  id: string;
  label: string;
  visible: boolean;
  onToggleVisibility: (id: string) => void;
  isHidden: boolean;
};

const NestedColumnRow: React.FC<NestedColumnRowProps> = ({
  id,
  label,
  visible,
  onToggleVisibility,
  isHidden,
}) => {
  const sortableId = `${SIDEBAR_L2CP_DND_PREFIX}${id}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'flex items-center gap-1.5 pl-4 pr-2 py-1.5 bg-gray-50 text-sm',
        isDragging && 'opacity-50 shadow-md',
        isHidden && 'hidden'
      )}
    >
      <button
        type="button"
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        tabIndex={0}
      >
        <Bars3Icon className="h-4 w-4" />
      </button>
      <span
        className={clsx(
          'flex-1 min-w-0 truncate select-none',
          visible ? 'text-gray-600' : 'text-gray-400'
        )}
        title={label}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={() => onToggleVisibility(id)}
        className={clsx(
          'flex-shrink-0 rounded p-0.5 transition-colors',
          visible ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 hover:text-gray-500'
        )}
        title={visible ? 'Hide column' : 'Show column'}
        aria-label={visible ? 'Hide column' : 'Show column'}
      >
        {visible ? (
          <EyeIcon className="h-3.5 w-3.5" />
        ) : (
          <EyeSlashIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
};

const ColumnGroup: React.FC<{
  label: string;
  children: React.ReactNode;
  visible: boolean;
}> = ({ label, children, visible }) => {
  if (!visible) return null;
  return (
    <div className="border-b border-gray-200 pb-2">
      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
};

export const ColumnManagerPanel: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const columns = useInventoryColumnStore((s) => s.columns);
  const pinnedLeft = useInventoryColumnStore((s) => s.pinnedLeft);
  const pinnedRight = useInventoryColumnStore((s) => s.pinnedRight);
  const toggleColumnVisibility = useInventoryColumnStore((s) => s.toggleColumnVisibility);
  const showAllColumns = useInventoryColumnStore((s) => s.showAllColumns);
  const hideAllColumns = useInventoryColumnStore((s) => s.hideAllColumns);
  const resetColumns = useInventoryColumnStore((s) => s.resetColumns);
  const isColumnsDefault = useInventoryColumnStore((s) => s.isColumnsDefault);
  const displayColumns = useMemo(() => buildDisplayColumns(columns), [columns]);
  const { activeDragId, activeDragSource, sidebarOverColumnId, sidebarInsertBefore } = useCrossDnd();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll sidebar to the actively-dragged table column as it reorders in real-time
  useEffect(() => {
    if (activeDragSource !== 'table' || !activeDragId || !scrollContainerRef.current) return;
    const sortableId = addSidebarPrefix(activeDragId);
    const container = scrollContainerRef.current;
    const row = container.querySelector(`[data-sortable-id="${sortableId}"]`) as HTMLElement | null;
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeDragSource, activeDragId, columns]);

  // Track drop indicator position in the scroll container
  const [indicatorTop, setIndicatorTop] = useState<number | null>(null);
  useEffect(() => {
    if (!sidebarOverColumnId || !activeDragId || sidebarOverColumnId === activeDragId || !scrollContainerRef.current) {
      setIndicatorTop(null);
      return;
    }
    // Find the DOM node for the over target inside the scroll container
    const container = scrollContainerRef.current;
    const rows = container.querySelectorAll('[data-sortable-id]');
    for (const row of rows) {
      if ((row as HTMLElement).dataset.sortableId === sidebarOverColumnId) {
        const rowEl = row as HTMLElement;
        // Position indicator above or below based on pointer position relative to row midpoint
        setIndicatorTop(sidebarInsertBefore ? rowEl.offsetTop : rowEl.offsetTop + rowEl.offsetHeight);
        return;
      }
    }
    setIndicatorTop(null);
  }, [sidebarOverColumnId, activeDragId, sidebarInsertBefore]);

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);

  const { pinnedLeftCols, pinnedRightCols, unpinnedCols } = useMemo(() => {
    const left: DisplayColumn[] = [];
    const right: DisplayColumn[] = [];
    const rest: DisplayColumn[] = [];
    for (const col of displayColumns) {
      if (pinnedLeftSet.has(col.id)) left.push(col);
      else if (pinnedRightSet.has(col.id)) right.push(col);
      else rest.push(col);
    }
    return { pinnedLeftCols: left, pinnedRightCols: right, unpinnedCols: rest };
  }, [displayColumns, pinnedLeftSet, pinnedRightSet]);

  const query = searchTerm.trim().toLowerCase();
  const matchesSearch = useCallback(
    (label: string): boolean => {
      if (!query) return true;
      return label.toLowerCase().includes(query);
    },
    [query]
  );
  /** L2CP nested column IDs in current store order, prefixed for the sortable context */
  const l2cpNestedSortableIds = useMemo(() => {
    const nestedSet: Set<string> = new Set(L2CP_NESTED_IDS);
    return columns
      .filter((c) => nestedSet.has(c.id))
      .map((c) => `${SIDEBAR_L2CP_DND_PREFIX}${c.id}`);
  }, [columns]);

  const renderColumnRow = (displayCol: DisplayColumn) => {
    const matches = matchesSearch(displayCol.label);
    const nestedMatches = displayCol.id === 'lentToCounterparties'
      ? L2CP_NESTED_IDS.some((nId) => {
          const nc = columns.find((c) => c.id === nId);
          return nc ? matchesSearch(nc.label) : false;
        })
      : false;
    const showGroup = matches || nestedMatches;
    return (
      <div key={displayCol.id === 'lentToCounterparties' ? 'l2cp-block' : displayCol.id}>
        <SortableColumnRow
          id={displayCol.id}
          label={displayCol.label}
          visible={displayCol.visible}
          onToggleVisibility={toggleColumnVisibility}
          isHidden={!showGroup}
          scrollContainerRef={scrollContainerRef}
        />
        {displayCol.id === 'lentToCounterparties' && (
          <SortableContext items={l2cpNestedSortableIds} strategy={noDisplacementStrategy}>
            <div className="space-y-0">
              {l2cpNestedSortableIds.map((sortableId) => {
                const nestedId = sortableId.slice(SIDEBAR_L2CP_DND_PREFIX.length);
                const nestedCol = columns.find((c) => c.id === nestedId);
                if (!nestedCol) return null;
                const nestedVisible = matchesSearch(nestedCol.label) || matches;
                return (
                  <NestedColumnRow
                    key={nestedId}
                    id={nestedCol.id}
                    label={nestedCol.label}
                    visible={nestedCol.visible}
                    onToggleVisibility={toggleColumnVisibility}
                    isHidden={!nestedVisible}
                  />
                );
              })}
            </div>
          </SortableContext>
        )}
      </div>
    );
  };

  const hasPinnedGroups = pinnedLeftCols.length > 0 || pinnedRightCols.length > 0;

  return (
    <div className="flex flex-1 flex-col gap-2 min-h-0">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search columns..."
          className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={showAllColumns}
          className="text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          Show all
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={hideAllColumns}
          className="text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          Hide all
        </button>
        {!isColumnsDefault() && (
          <>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={resetColumns}
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              Reset
            </button>
          </>
        )}
      </div>
      <div ref={scrollContainerRef} className="-mr-6 flex-1 overflow-y-auto space-y-0.5 pr-6 relative">
        {indicatorTop !== null && (
          <div
            className="absolute left-0 right-6 h-0.5 bg-blue-500 rounded-full z-10 pointer-events-none transition-[top] duration-75 ease-out"
            style={{ top: `${indicatorTop}px` }}
          />
        )}
        <SortableContext
          items={displayColumns.map((d) => addSidebarPrefix(d.id))}
          strategy={noDisplacementStrategy}
        >
          {hasPinnedGroups ? (
            <div className="space-y-3">
              <ColumnGroup label="Pinned Left" visible={pinnedLeftCols.length > 0}>
                {pinnedLeftCols.map(renderColumnRow)}
              </ColumnGroup>
              <ColumnGroup label="Pinned Right" visible={pinnedRightCols.length > 0}>
                {pinnedRightCols.map(renderColumnRow)}
              </ColumnGroup>
              <ColumnGroup label="Columns" visible={unpinnedCols.length > 0}>
                {unpinnedCols.map(renderColumnRow)}
              </ColumnGroup>
            </div>
          ) : (
            displayColumns.map(renderColumnRow)
          )}
        </SortableContext>
      </div>
    </div>
  );
};
